// v17.10 — WebSocket Multiplex Hub singleton (frontend side)
//
// MỤC TIÊU: Gộp 6 kết nối WS/user (match / tournament / notifications /
// admin:hls-jobs / admin:publisher-battery / livestream:auto) về 1 socket
// duy nhất → ở 500 CCU giảm ~2500 socket VPS.
//
// API:
//   import { subscribe, getState } from "../lib/wsHub";
//
//   const unsub = subscribe("match:abc123", (data) => { ... });
//   // later:
//   unsub();
//
// Chi tiết:
//   • Singleton lười tạo — kết nối mở khi có subscriber đầu tiên.
//   • Ref-count subscribers/channel → chỉ gửi 1 `subscribe` cho mỗi channel,
//     và `unsubscribe` khi listener cuối cùng huỷ.
//   • Auth ticket lấy qua `/auth/ws-ticket` (đồng nhất với các hook cũ).
//     Ticket hết hạn hoặc 4401/4403 → fetch ticket mới rồi reconnect.
//   • Reconnect exponential 1s→30s + jitter, resubscribe TẤT CẢ channel
//     ngay khi kết nối lại.
//   • Server ping mỗi 30s (idle timeout của hub); client cũng chủ động
//     gửi `{op:"ping"}` mỗi 60s để phát hiện dead connection sớm hơn
//     `close` event trên mobile.
//   • Server response cho subscribe: `{type:"ack", op:"subscribe",
//     channel, ok, error}` — hook không cần chờ ack (best-effort).
//   • Payload từ server: `{channel, data}` — hub dispatch `data` cho handler.

import { api, BACKEND_WS_URL } from "./api";
import { hasAuthHint } from "./authHint";
import { isEmbedRuntime } from "./embedMode";

const PING_INTERVAL_MS = 60000;
const RECONNECT_MIN_MS = 1000;
const RECONNECT_MAX_MS = 30000;
let _visReconnectArmed = false;

const state = {
  ws: null,
  connState: "closed", // "closed" | "connecting" | "open" | "auth_failed"
  // channel -> Set(handler)
  handlers: new Map(),
  // channel -> boolean (đã gửi subscribe frame chưa)
  subscribed: new Set(),
  reconnectAttempt: 0,
  reconnectTimer: null,
  pingTimer: null,
};

function _wsBase() {
  const proto =
    typeof window !== "undefined" && window.location.protocol === "https:"
      ? "wss:"
      : "ws:";
  return (
    BACKEND_WS_URL ||
    (typeof window !== "undefined" ? `${proto}//${window.location.host}` : "")
  );
}

async function _getTicket() {
  // Cookie auth vẫn hoạt động cho public channel; ticket cần cho notifications
  // + admin channels. Không có ticket → vẫn kết nối được (guest).
  // PR-B: guest không có auth hint → khỏi gọi /auth/ws-ticket (chắc chắn 401
  // vô ích + có thể kích refresh interceptor) → connect thẳng kiểu guest.
  if (!hasAuthHint()) return null;
  try {
    const r = await api.get("/auth/ws-ticket");
    return r?.data?.ticket || null;
  } catch (err) {
    // Log 1 lần cho mỗi phiên (không spam mỗi reconnect).
    if (!_ticketFailReported) {
      _ticketFailReported = true;
      try {
        // eslint-disable-next-line no-console
        console.warn("[wsHub] ws-ticket fetch failed — fallback cookie auth:", err?.message || err);
      } catch { /* noop */ }
    }
    return null;
  }
}
let _ticketFailReported = false;

function _sendRaw(obj) {
  const ws = state.ws;
  if (!ws || ws.readyState !== 1 /* OPEN */) return false;
  try {
    ws.send(JSON.stringify(obj));
    return true;
  } catch {
    return false;
  }
}

function _sendSubscribe(channel) {
  if (state.subscribed.has(channel)) return;
  if (_sendRaw({ op: "subscribe", channel })) {
    state.subscribed.add(channel);
  }
}

function _sendUnsubscribe(channel) {
  if (!state.subscribed.has(channel)) return;
  _sendRaw({ op: "unsubscribe", channel });
  state.subscribed.delete(channel);
}

function _dispatch(channel, data) {
  const set = state.handlers.get(channel);
  if (!set) return;
  for (const h of set) {
    try {
      h(data);
    } catch {
      /* noop — 1 handler lỗi không ảnh hưởng handler khác */
    }
  }
}

function _clearReconnectTimer() {
  if (state.reconnectTimer) {
    clearTimeout(state.reconnectTimer);
    state.reconnectTimer = null;
  }
}

function _stopPing() {
  if (state.pingTimer) {
    clearInterval(state.pingTimer);
    state.pingTimer = null;
  }
}

function _scheduleReconnect() {
  if (state.reconnectTimer) return;
  if (state.handlers.size === 0) return; // không còn subscriber → khỏi reconnect
  // PR-G1 (GĐ2 audit) — tab ẩn → KHÔNG reconnect (đỡ N background tab cùng
  // dội handshake khi BE restart). Arm 1 listener chờ tab visible lại.
  if (typeof document !== "undefined" && document.hidden) {
    if (_visReconnectArmed) return;
    _visReconnectArmed = true;
    const onVis = () => {
      if (document.hidden) return;
      document.removeEventListener("visibilitychange", onVis);
      _visReconnectArmed = false;
      _scheduleReconnect();
    };
    document.addEventListener("visibilitychange", onVis);
    return;
  }
  const attempt = Math.min(state.reconnectAttempt + 1, 6);
  state.reconnectAttempt = attempt;
  const base = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** (attempt - 1));
  const jitter = Math.random() * 500;
  const delay = base + jitter;
  state.reconnectTimer = setTimeout(() => {
    state.reconnectTimer = null;
    // Đợt Offline-1 — governor gate: offline / trước cửa sổ WS (5-25s) → chờ.
    try {
      const { canRun } = require("./networkGovernor");
      if (!canRun("ws")) { _scheduleReconnect(); return; }
    } catch { /* noop */ }
    _connect().catch(() => _scheduleReconnect());
  }, delay);
}

async function _connect() {
  if (state.connState === "connecting" || state.connState === "open") return;
  state.connState = "connecting";
  state.subscribed.clear();

  // v17.19 — Traffic control DANGER flag: ws_realtime OFF → không mở hub;
  // reconnect loop sẽ thử lại và tự nối khi flag bật lại (poll flags 120s).
  try {
    const { getTrafficFlag } = require("./trafficFlags");
    if (!getTrafficFlag("ws_realtime")) {
      state.connState = "closed";
      _scheduleReconnect();
      return;
    }
  } catch { /* noop */ }

  const wsBase = _wsBase();
  const ticket = await _getTicket();
  const url = ticket
    ? `${wsBase}/api/ws/hub?token=${encodeURIComponent(ticket)}`
    : `${wsBase}/api/ws/hub`;

  let ws;
  try {
    ws = new WebSocket(url);
  } catch {
    state.connState = "closed";
    _scheduleReconnect();
    return;
  }
  state.ws = ws;

  ws.onopen = () => {
    state.connState = "open";
    state.reconnectAttempt = 0;
    // Resubscribe TẤT CẢ channel đang active.
    for (const ch of state.handlers.keys()) {
      _sendRaw({ op: "subscribe", channel: ch });
      state.subscribed.add(ch);
    }
    // Ping loop
    _stopPing();
    state.pingTimer = setInterval(() => {
      _sendRaw({ op: "ping" });
    }, PING_INTERVAL_MS);
  };

  ws.onmessage = (ev) => {
    try {
      const msg = JSON.parse(ev.data);
      if (!msg || typeof msg !== "object") return;
      // Server may push {channel,data} for real events, or
      // {type:"connected"|"ack"|"ping"|"pong"} for control frames.
      if (msg.channel && "data" in msg) {
        _dispatch(msg.channel, msg.data);
        return;
      }
      // ACK failure → xử lý phân biệt lỗi.
      if (msg.type === "ack" && msg.op === "subscribe" && msg.ok === false) {
        if (msg.error === "auth_required") {
          // Đang guest connect nhưng channel cần user_id (notifications) —
          // fetch ticket mới rồi reconnect. Giữ handler để resub sau khi
          // auth thành công.
          state.subscribed.delete(msg.channel);
          try {
            if (state.ws) state.ws.close(4001, "reauth");
          } catch {
            /* noop */
          }
        } else if (msg.error === "not_authorized") {
          // User đăng nhập nhưng thiếu role admin — bỏ handler tránh spam
          // resub. UI hook cần tự check quyền trước khi subscribe channel này.
          state.handlers.delete(msg.channel);
          state.subscribed.delete(msg.channel);
        } else if (msg.error === "invalid_channel" || msg.error === "unknown_channel" || msg.error === "too_many_channels") {
          // Sai channel / vượt trần channel — bỏ handler tránh spam resub.
          state.handlers.delete(msg.channel);
          state.subscribed.delete(msg.channel);
        }
      }
    } catch {
      /* ignore malformed */
    }
  };

  ws.onerror = () => {
    try {
      ws.close();
    } catch {
      /* noop */
    }
  };

  ws.onclose = (ev) => {
    _stopPing();
    state.ws = null;
    state.subscribed.clear();
    state.connState = "closed";
    const code = ev?.code || 0;
    // Auth close codes (4401/4403): KHÔNG tăng reconnectAttempt vì mỗi lần
    // reconnect ta luôn fetch ticket mới — inflate backoff sẽ khiến user vừa
    // login xong phải chờ ~30s để kết nối.
    if (code === 4401 || code === 4403 || code === 4001 /* reauth */) {
      state.reconnectAttempt = 0;
    }
    if (state.handlers.size > 0) _scheduleReconnect();
  };
}

/**
 * Đăng ký handler cho channel. Trả về hàm huỷ đăng ký.
 * Có thể gọi `subscribe(channel, handler)` nhiều lần cho cùng handler mà
 * không sợ trùng — Set tự dedupe.
 */
export function subscribe(channel, handler) {
  if (!channel || typeof handler !== "function") return () => {};
  // PR-A: iframe ?embed=1 → KHÔNG mở WS (reduced-runtime embed). Overlay tỷ
  // số trong embed dựa vào burn-in hoặc timeline fetch ban đầu.
  if (isEmbedRuntime()) return () => {};
  let set = state.handlers.get(channel);
  const firstListener = !set;
  if (!set) {
    set = new Set();
    state.handlers.set(channel, set);
  }
  set.add(handler);

  // Ensure connection is being established.
  if (state.connState === "closed") {
    _connect().catch(() => _scheduleReconnect());
  } else if (state.connState === "open" && firstListener) {
    _sendSubscribe(channel);
  }
  // Nếu đang connecting → onopen sẽ resubscribe tất cả channels.

  return () => {
    const s = state.handlers.get(channel);
    if (!s) return;
    s.delete(handler);
    if (s.size === 0) {
      state.handlers.delete(channel);
      if (state.connState === "open") {
        _sendUnsubscribe(channel);
      }
      // Không đóng socket ngay khi hết listener — giữ ấm 60s để tránh
      // reconnect thrash khi user chuyển nhanh giữa các trang.
      if (state.handlers.size === 0) {
        _scheduleIdleClose();
      }
    }
  };
}

let _idleCloseTimer = null;
function _scheduleIdleClose() {
  if (_idleCloseTimer) clearTimeout(_idleCloseTimer);
  _idleCloseTimer = setTimeout(() => {
    _idleCloseTimer = null;
    if (state.handlers.size > 0) return; // có subscriber mới → hủy đóng
    _clearReconnectTimer();
    _stopPing();
    if (state.ws) {
      try {
        state.ws.close(1000, "idle");
      } catch {
        /* noop */
      }
    }
    state.ws = null;
    state.connState = "closed";
    state.reconnectAttempt = 0;
  }, 60000);
}

export function getState() {
  return {
    connState: state.connState,
    channels: Array.from(state.handlers.keys()),
    subscribed: Array.from(state.subscribed),
    reconnectAttempt: state.reconnectAttempt,
  };
}

// Optional: cho phép hook khác chủ động ngắt kết nối khi logout.
export function _resetForTest() {
  _clearReconnectTimer();
  _stopPing();
  if (_idleCloseTimer) {
    clearTimeout(_idleCloseTimer);
    _idleCloseTimer = null;
  }
  if (state.ws) {
    try {
      state.ws.close(1000, "reset");
    } catch {
      /* noop */
    }
  }
  state.ws = null;
  state.connState = "closed";
  state.handlers.clear();
  state.subscribed.clear();
  state.reconnectAttempt = 0;
}
